import { LinkIcon } from '@chakra-ui/icons';
import { filter, get, intersection } from 'lodash';
import { getI18n } from 'react-i18next';
import { AiFillCar, AiOutlineEuroCircle, AiOutlineForm } from 'react-icons/ai';
import { BsListTask } from 'react-icons/bs';
import { FiBarChart2, FiCheck, FiClock, FiGrid, FiInfo } from 'react-icons/fi';
import { GiComputing } from 'react-icons/gi';
import { IoIosStats } from 'react-icons/io';
import { LuFileCheck2, LuFileClock } from 'react-icons/lu';
import { MdOutlineSwitchAccount } from 'react-icons/md';
import { MdOutlineSupervisedUserCircle } from 'react-icons/md';
import { MdAnnouncement, MdGavel } from 'react-icons/md';
import { RiListCheck2 } from 'react-icons/ri';
import { TbRobot } from 'react-icons/tb';
import { TbDiscount } from 'react-icons/tb';
import { VscOrganization } from 'react-icons/vsc';

export const TaskTypeMappingToRoute = {
  service_entry: 'service-record',
  service_entry_deletion: 'service-record',
  workshop_data_update: 'workshop-update-request',
  verification: 'workshop-verification-request',
  account_registration: 'account',
  account_deletion: 'account',
  legal_documents: 'legal-documents',
};
export enum UserPermissions {
  TASKSVIEW = 'tasks:view',
  USERSVIEW = 'users:view',
  WORKSHOPSVIEW = 'workshops:view',
  CHAINSVIEW = 'chains:view',
  PROMOTIONSVIEW = 'promotions:view',
  ANNOUNCEMENTSVIEW = 'announcements:view',
  DSBFORMSVIEW = 'dsb-forms:view',
  WHITELABELORGANIZATIONSVIEW = 'whitelabel-organizsations:view',
  WHITELABELORGANIZATIONSADMINUSERVIEW = 'wl-admin-users:view',
  ACCOUNTSVIEW = 'accounts:view',
  SERVICEENTRIES = 'service-entries:view',
  SERVICEHISTORYREQUESTSVIEW = 'service-history-requests:view',
  WORKSHOPVERIFICATIONREQUESTSVIEW = 'workshop-verification-requests:view',
  WORKSHOPDATAUPDATEREQUESTSVIEW = 'workshop-data-update-requests:view',
  MAKESCOUNTRYMAPPINGVIEW = 'make-country-mappings:view',
  MANUFACTURERSVIEW = 'manufacturers:view',
  CONTROLLINGOPSVIEW = 'controlling-ops:view',
  CONTROLLINGSTATISTICSVIEW = 'controlling-statistics:view',
  CONTROLLINGFINANCIALSVIEW = 'controlling-financials:view',
  LEGALDOCUMENTSVIEW = 'legal-document-versions:view',
  SERVICEENTRYCHANGEVIN = 'service-entry:change-vin',
  AUTOMATIONSCONTROL = 'automations:control',
  VERIFICATIONSMANUALLYUPLOADPOA = 'verifications:manually-upload-poa',
}

export enum UserRoles {
  ADMIN = 'admin',
  OPERATIONS = 'operations',
  CONTROLLING = 'controlling',
  SETTINGS = 'settings',
  DEVELOPMENT = 'development',
  BOARD = 'board',
  MARKETING = 'marketing',
  MANAGEMENT = 'management',
}

export enum RouteIds {
  DASHBOARD = 'dashboard',
  WORKSHOPS = 'workshops',
  CHAINS = 'chains',
  VERIFICATIONS = 'verifications',
  SERVICE_RECORDS = 'serviceRecords',
  SERVICE_HISTORY = 'serviceHistory',
  DATA_CHANGE = 'dataChange',
  LEGAL_DOCUMENTS = 'legalDocuments',
  PROMOTIONS = 'promotions',
  ANNOUNCEMENTS = 'announcements',
  UTILIZATION = 'utilization',
  STATISTICS = 'statistics',
  FINANCIALS = 'financials',
  FORMS_CONFIG = 'forms',
  MAKES_CONFIG = 'makes_config',
  MAKES = 'makes',
  TASKS = 'tasks',
  ACCOUNTS = 'accounts',
  USERS = 'users',
  PERSONAL_SETTING = 'personalSettings',
  WHITELABEL = 'whitelabelOrganizations',
  AUTOMATIONS = 'automations',
}

export const routesByGroup = {
  general: {
    label: getI18n().t('sidebar:general.label'),
    routes: [
      {
        id: RouteIds.DASHBOARD,
        label: getI18n().t('sidebar:general.dashboard'),
        icon: FiGrid,
        to: '/app/op/dashboard',
        accessibleBy: Object.values(UserPermissions),
      },
      {
        id: RouteIds.WORKSHOPS,
        label: getI18n().t('sidebar:general.workshops'),
        icon: FiBarChart2,
        to: '/app/op/workshops',
        accessibleBy: [UserPermissions.WORKSHOPSVIEW],
      },
      {
        id: RouteIds.CHAINS,
        label: getI18n().t('sidebar:general.chains'),
        icon: LinkIcon,
        to: '/app/op/chains',
        accessibleBy: [UserPermissions.CHAINSVIEW],
      },
    ],
  },
  operations: {
    label: getI18n().t('sidebar:operations.label'),
    routes: [
      {
        id: RouteIds.TASKS,
        label: getI18n().t('sidebar:operations.tasks'),
        icon: BsListTask,
        to: '/app/op/tasks',
        accessibleBy: [UserPermissions.TASKSVIEW],
      },
      {
        id: RouteIds.ACCOUNTS,
        label: getI18n().t('sidebar:operations.accounts'),
        icon: MdOutlineSwitchAccount,
        to: '/app/op/accounts',
        accessibleBy: [UserPermissions.ACCOUNTSVIEW],
      },
      {
        id: RouteIds.VERIFICATIONS,
        label: getI18n().t('sidebar:operations.workshop_verification_request'),
        icon: FiCheck,
        to: '/app/op/workshop-verification-request',
        accessibleBy: [UserPermissions.WORKSHOPVERIFICATIONREQUESTSVIEW],
      },
      {
        id: RouteIds.VERIFICATIONS,
        label: getI18n().t('sidebar:operations.b2c_verification_request'),
        icon: LuFileCheck2,
        to: '/app/op/b2c-verification-request',
        accessibleBy: [UserPermissions.WORKSHOPVERIFICATIONREQUESTSVIEW],
      },

      {
        id: RouteIds.SERVICE_RECORDS,
        label: getI18n().t('sidebar:operations.service_record'),
        icon: RiListCheck2,
        to: '/app/op/service-record',
        accessibleBy: [UserPermissions.SERVICEENTRIES],
      },

      {
        id: RouteIds.SERVICE_HISTORY,
        label: getI18n().t('sidebar:operations.service_history_request'),
        icon: FiClock,
        to: '/app/op/service-history-request',
        accessibleBy: [UserPermissions.SERVICEHISTORYREQUESTSVIEW],
      },

      {
        id: RouteIds.SERVICE_HISTORY,
        label: getI18n().t('sidebar:operations.b2c_service_history_request'),
        icon: LuFileClock,
        to: '/app/op/b2c-service-history-request',
        accessibleBy: [UserPermissions.SERVICEHISTORYREQUESTSVIEW],
      },

      {
        id: RouteIds.DATA_CHANGE,
        label: getI18n().t('sidebar:operations.data_change'),
        icon: FiInfo,
        to: '/app/op/workshop-update-request',
        accessibleBy: [UserPermissions.WORKSHOPDATAUPDATEREQUESTSVIEW],
      },
      {
        id: RouteIds.LEGAL_DOCUMENTS,
        label: getI18n().t('sidebar:operations.legal_documents'),
        icon: MdGavel,
        to: '/app/op/legal-documents',
        accessibleBy: [UserPermissions.LEGALDOCUMENTSVIEW],
      },
    ],
  },
  settings: {
    label: getI18n().t('sidebar:settings.label'),
    routes: [
      {
        id: RouteIds.MAKES,
        label: getI18n().t('sidebar:settings.makes'),
        icon: RiListCheck2,
        to: '/app/op/config/makes',
        accessibleBy: [UserPermissions.MAKESCOUNTRYMAPPINGVIEW],
      },
      {
        id: RouteIds.MAKES_CONFIG,
        label: getI18n().t('sidebar:settings.makes_mapping'),
        icon: AiOutlineForm,
        to: '/app/op/config/makes-mapping',
        accessibleBy: [UserPermissions.MAKESCOUNTRYMAPPINGVIEW],
      },
      {
        id: RouteIds.FORMS_CONFIG,
        label: getI18n().t('sidebar:settings.forms_config'),
        icon: AiFillCar,
        to: '/app/op/dsb-form',
        accessibleBy: [UserPermissions.DSBFORMSVIEW],
      },
      {
        id: RouteIds.PROMOTIONS,
        label: getI18n().t('sidebar:settings.promotions'),
        icon: TbDiscount,
        to: '/app/op/promotions',
        accessibleBy: [UserPermissions.PROMOTIONSVIEW],
      },
      {
        id: RouteIds.ANNOUNCEMENTS,
        label: getI18n().t('sidebar:settings.announcements'),
        icon: MdAnnouncement,
        to: '/app/op/announcements',
        accessibleBy: [UserPermissions.ANNOUNCEMENTSVIEW],
      },
      {
        id: RouteIds.USERS,
        label: getI18n().t('sidebar:settings.users'),
        icon: MdOutlineSupervisedUserCircle,
        to: '/app/op/users',
        accessibleBy: [UserPermissions.USERSVIEW],
      },
      {
        id: RouteIds.WHITELABEL,
        label: getI18n().t('sidebar:operations.whitelabel_organizations'),
        icon: VscOrganization,
        to: '/app/op/whitelabel-organizations',
        accessibleBy: [UserPermissions.WHITELABELORGANIZATIONSADMINUSERVIEW],
      },
      {
        id: RouteIds.AUTOMATIONS,
        label: getI18n().t('sidebar:settings.automations'),
        icon: TbRobot,
        to: '/app/op/automations',
        accessibleBy: [UserPermissions.AUTOMATIONSCONTROL],
      },
    ],
  },
  controlling: {
    label: getI18n().t('sidebar:controlling.label'),
    routes: [
      {
        id: RouteIds.UTILIZATION,
        label: getI18n().t('sidebar:controlling.utilization'),
        icon: GiComputing,
        to: '/app/op/utilization',
        accessibleBy: [UserPermissions.CONTROLLINGOPSVIEW],
      },
      {
        id: RouteIds.STATISTICS,
        label: getI18n().t('sidebar:controlling.statistics'),
        icon: IoIosStats,
        to: '/app/op/statistics',
        accessibleBy: [UserPermissions.CONTROLLINGSTATISTICSVIEW],
      },
      {
        id: RouteIds.FINANCIALS,
        label: getI18n().t('sidebar:financials.label'),
        icon: AiOutlineEuroCircle,
        to: '/app/op/cashflow',
        accessibleBy: [UserPermissions.CONTROLLINGFINANCIALSVIEW],
      },
    ],
  },
};

export const getAvailableGroups = (groups: any[] = []) => {
  const groupsForUser = routesByGroup;
  Object.keys(routesByGroup).forEach((groupKey) => {
    const filteredRoutes = filter(
      //@ts-ignore
      groupsForUser[groupKey].routes,
      (route) =>
        intersection(
          //@ts-ignore
          get(route, 'accessibleBy', groupsForUser[groupKey].accessibleBy),
          groups,
        ).length > 0,
    );
    if (filteredRoutes.length === 0) {
      //@ts-ignore
      delete groupsForUser[groupKey];
    } else {
      //@ts-ignore
      groupsForUser[groupKey].routes = filteredRoutes;
    }
  });
  return Object.values(groupsForUser);
};
